import {
  cacheKeys,
  Media,
  MediaType,
  useCreateMutation,
  useUpdateMutation,
  useWorkspaceAsset,
  useWorkspaceCategories,
} from '@gripp/shared-logic';
import {
  Button,
  CategorySelector,
  ConfirmationOverlay,
  ControlledTextInput,
  Spacing,
  useSlideLayoutSafe,
} from '@gripp/shared-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import * as yup from 'yup';
import { object as yupObject } from 'yup';

const UPDATE_FILE = `
  mutation UpdateFile($input: MutateMediaInput!) {
    updateMedia(input: $input) {
      id
    }
  }
`;

const CREATE_CATEGORY = `
  mutation createCategory($input: CreateCategoryInput!) {
    createCategory(input: $input) {
      id
      name
    }
  }
`;

const UPDATE_ASSET = `
  mutation UpdateAsset($input: MutateAssetInput!) {
    updateAsset(input: $input) {
      id
    }
  }
`;

type CategoryItem = { id: string; name: string };

type AssetFileEditFormProps = {
  media: Media;
  assetId: string;
  allFileIds: string[];
  onComplete: () => void;
  onDirtyChange?: (isDirty: boolean) => void;
  onCategoryEdit?: () => void;
  selectedCategories?: CategoryItem[];
  onCategoriesChange?: (cats: CategoryItem[]) => void;
};

const buildSchema = (isLink: boolean) =>
  yupObject({
    title: yup.string().required(),
    linkUrl: isLink ? yup.string().url().required() : yup.string().optional(),
    categories: yup
      .array()
      .of(
        yupObject({
          id: yup.string().required(),
          name: yup.string().required(),
        })
      )
      .optional(),
  });

export const AssetFileEditForm = ({
  media,
  assetId,
  allFileIds,
  onComplete,
  onDirtyChange,
  onCategoryEdit,
  selectedCategories: externalCategories,
  onCategoriesChange,
}: AssetFileEditFormProps) => {
  const { t } = useTranslation();
  const { categories: availableCategories } = useWorkspaceCategories();
  const { invalidateQuery } = useWorkspaceAsset(assetId);
  const queryClient = useQueryClient();
  const slideLayout = useSlideLayoutSafe();
  const { mutateAsync: updateFile } = useUpdateMutation({
    modelName: 'media',
    query: UPDATE_FILE,
  });
  const { mutateAsync: updateAsset } = useUpdateMutation({
    modelName: 'asset',
    query: UPDATE_ASSET,
  });
  const { mutateAsync: createCategory } = useCreateMutation({
    modelName: 'category',
    query: CREATE_CATEGORY,
  });

  const isLink = media.type === MediaType.Link;
  const schema = buildSchema(isLink);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { isValid, isDirty, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      title: media.name ?? '',
      linkUrl: media.url ?? '',
      categories: (media.categories as { id: string; name: string }[]) ?? [],
    },
  });

  const selectedCategories =
    (watch('categories') as { id: string; name: string }[]) ?? [];

  // Sync external controlled categories → form (driven by EditCategory panel or parent)
  useEffect(() => {
    if (!externalCategories) return;
    const formIds = selectedCategories
      .map((c) => c.id)
      .sort()
      .join(',');
    const extIds = externalCategories
      .map((c) => c.id)
      .sort()
      .join(',');
    if (formIds !== extIds) {
      setValue('categories', externalCategories, { shouldDirty: true });
    }
  }, [externalCategories]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const handleCreateCategory = useCallback(
    async (name: string) => {
      const result = await createCategory({ input: { name } });
      return { id: result.id, name };
    },
    [createCategory]
  );

  const onSubmit = async (data: {
    title: string;
    linkUrl?: string;
    categories?: { id: string; name: string }[];
  }) => {
    const input: Record<string, unknown> = {
      id: media.id,
      name: data.title,
      categories: (data.categories ?? []).map((c) => ({ id: c.id })),
    };

    if (isLink && data.linkUrl) {
      input['url'] = data.linkUrl;
    }

    await updateFile({ input });

    const mediaFile = allFileIds.map((id) => ({ id }));
    await updateAsset({ input: { id: assetId, media: mediaFile } });

    await invalidateQuery();
    onComplete();
  };

  const hideDeleteOverlay = () => slideLayout?.setColumnOverlay(null);

  const handleDeleteConfirm = async () => {
    const fileList = allFileIds
      .filter((id) => id !== media.id)
      .map((id) => ({ id }));
    await updateAsset(
      { input: { id: assetId, media: fileList } },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: cacheKeys('asset').all,
          });
          hideDeleteOverlay();
          onComplete();
        },
      }
    );
  };

  const handleDeletePress = () => {
    slideLayout?.setColumnOverlay(
      <ConfirmationOverlay
        title={t('asset.detail.confirmDelete', { fileName: media.name })}
        subtitle={t('asset.detail.delete_warning')}
        confirmLabel={t('topNav.customAlert.yes')}
        onConfirm={handleDeleteConfirm}
        cancelLabel={t('topNav.customAlert.cancel')}
        onCancel={hideDeleteOverlay}
      />
    );
  };

  const canSave = isDirty && isValid && !isSubmitting;

  return (
    <ScrollView
      contentContainerStyle={styles.scrollContent}
      showsVerticalScrollIndicator={false}
    >
      <ControlledTextInput
        control={control}
        name="title"
        placeholder={t('file.create.title')}
        useCropCircle
        hideBorder
      />
      {isLink && (
        <ControlledTextInput
          control={control}
          name="linkUrl"
          placeholder={t('file.create.linkUrl')}
          useCropCircle
          hideBorder
        />
      )}
      <CategorySelector
        selectedCategories={selectedCategories}
        availableCategories={availableCategories ?? []}
        onCategoriesChange={(cats) => {
          setValue('categories', cats, { shouldDirty: true });
          onCategoriesChange?.(cats);
        }}
        onCreateCategory={handleCreateCategory}
        useCropCircle
        onSelectorEdit={onCategoryEdit ?? slideLayout?.toggleThirdPanel}
      />
      <View style={styles.contentActions}>
        <Button
          style={styles.saveButton}
          onPress={canSave ? handleSubmit(onSubmit) : undefined}
          state={canSave ? 'active' : 'default'}
          disabled={!canSave}
          size="large"
        >
          {t('asset.save')}
        </Button>
        <Button
          variant="rectangular"
          state="outline"
          style={styles.deleteButton}
          onPress={handleDeletePress}
          size="default"
        >
          {t('file.deleteFile.deleteFile')}
        </Button>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  scrollContent: {
    flexGrow: 1,
    padding: Spacing.basePadding.base,
    gap: 14,
  },
  contentActions: {
    marginTop: 'auto',
    paddingTop: Spacing.basePadding.base * 2,
    gap: Spacing.baseMargin.base,
  },
  saveButton: {
    borderRadius: 2,
    width: '100%',
    height: 48,
  },
  deleteButton: {
    borderWidth: 0,
    width: '100%',
  },
});

